import { openDB, type DBSchema, type IDBPDatabase } from 'idb'
import { arrayBufferToBase64, base64ToArrayBuffer } from './base64.ts'
import { ecdsaP1363ToDer } from './ecdsaDer.ts'

interface SocketDb extends DBSchema {
  keys: {
    key: string
    value: string
  }
}

let dbPromise: Promise<IDBPDatabase<SocketDb>> | null = null

function getDb(): Promise<IDBPDatabase<SocketDb>> {
  if (!dbPromise) {
    dbPromise = openDB<SocketDb>('zion-socket-auth', 1, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('keys')) db.createObjectStore('keys')
      },
    })
  }
  return dbPromise
}

const EC_SIGN: EcKeyGenParams & EcKeyAlgorithm = { name: 'ECDSA', namedCurve: 'P-256' }

export async function ensureSocketAuthKeyPair(): Promise<CryptoKeyPair> {
  const db = await getDb()
  const privB64 = await db.get('keys', 'private_pkcs8')
  const pubB64 = await db.get('keys', 'public_spki')
  if (privB64 && pubB64) {
    const privateKey = await crypto.subtle.importKey(
      'pkcs8',
      base64ToArrayBuffer(privB64),
      EC_SIGN,
      true,
      ['sign'],
    )
    const publicKey = await crypto.subtle.importKey(
      'spki',
      base64ToArrayBuffer(pubB64),
      EC_SIGN,
      true,
      ['verify'],
    )
    return { privateKey, publicKey }
  }

  const keyPair = await crypto.subtle.generateKey(
    { ...EC_SIGN },
    true,
    ['sign', 'verify'],
  )
  const privatePkcs8 = await crypto.subtle.exportKey('pkcs8', keyPair.privateKey)
  const publicSpki = await crypto.subtle.exportKey('spki', keyPair.publicKey)
  await db.put('keys', arrayBufferToBase64(privatePkcs8), 'private_pkcs8')
  await db.put('keys', arrayBufferToBase64(publicSpki), 'public_spki')
  return keyPair
}

export async function getSocketAuthPublicKeyBase64(): Promise<string> {
  const { publicKey } = await ensureSocketAuthKeyPair()
  const spki = await crypto.subtle.exportKey('spki', publicKey)
  return arrayBufferToBase64(spki)
}

export function socketAuthTimestamp(): string {
  return String(Date.now())
}

export async function socketAuthSign(privateKey: CryptoKey, id: string, ts: string): Promise<string> {
  const payload = new TextEncoder().encode(`${id}:${ts}`)
  const rawSig = await crypto.subtle.sign({ name: 'ECDSA', hash: { name: 'SHA-256' } }, privateKey, payload)
  const derSig = ecdsaP1363ToDer(rawSig)
  return arrayBufferToBase64(derSig)
}

export async function socketAuthSigned(
  privateKey: CryptoKey,
  id: string,
): Promise<{ ts: string; sig: string }> {
  const ts = socketAuthTimestamp()
  const sig = await socketAuthSign(privateKey, id, ts)
  return { ts, sig }
}

export async function socketAuthSignRaw(privateKey: CryptoKey, payload: string): Promise<string> {
  const data = new TextEncoder().encode(payload)
  const rawSig = await crypto.subtle.sign({ name: 'ECDSA', hash: { name: 'SHA-256' } }, privateKey, data)
  return arrayBufferToBase64(ecdsaP1363ToDer(rawSig))
}
